import { t } from '../../i18n/es';
import type { Settings } from '../../save/schema';
import { SETTING_CONTROLS, VOLUME_STEPS, percentToVolume, volumeToPercent } from './settings-model';

export interface SettingsScreenOptions {
  settings: () => Settings;
  update: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  onBack: () => void;
}

/**
 * AJUSTES (GDD §13.6, issue #63): the five options, each change written at once (GDD §6.4) and applied by
 * whoever listens to the settings (audio, FPS counter, motion of the interface). Volumes are selects in
 * steps of 10 % until the design system has a slider; toggles are switches. "Volver" goes back to the
 * screen that opened it. Native elements until the design system lands (#15).
 */
export function ajustesScreen({ settings, update, onBack }: SettingsScreenOptions): HTMLElement {
  const current = settings();
  const section = document.createElement('section');
  section.className = 'panel settings';
  section.setAttribute('aria-labelledby', 'settings-title');
  section.innerHTML = `
    <h1 id="settings-title" class="tactical">${t('ui.screen.ajustes')}</h1>
    <div class="settings-list">${SETTING_CONTROLS.map((control) => {
      const id = `setting-${control.key}`;
      if (control.kind === 'volume') {
        const value = volumeToPercent(current[control.key]);
        return `
      <div class="setting"><label for="${id}">${t(control.label)}</label>
        <select id="${id}" class="readout" data-setting="${control.key}">${VOLUME_STEPS.map(
          (step) => `<option value="${step}"${step === value ? ' selected' : ''}>${step} %</option>`,
        ).join('')}</select>
      </div>`;
      }
      return `
      <div class="setting"><label for="${id}">${t(control.label)}</label>
        <input id="${id}" type="checkbox" role="switch" data-setting="${control.key}"${current[control.key] ? ' checked' : ''}${control.key === 'assistMode' ? ` aria-describedby="${id}-help"` : ''} />
        ${control.key === 'assistMode' ? `<p id="${id}-help" class="muted" data-field="assist-help">${t('ui.ajustes.assistMode.help')}</p>` : ''}
      </div>`;
    }).join('')}</div>
    <div class="slot-actions">
      <button type="button" class="button" data-action="back">${t('ui.ajustes.back')}</button>
    </div>`;

  section.addEventListener('change', (event) => {
    const control = event.target as HTMLInputElement | HTMLSelectElement;
    const key = control.dataset.setting as keyof Settings | undefined;
    if (!key) return;
    if (control instanceof HTMLSelectElement) update(key as 'musicVolume' | 'sfxVolume', percentToVolume(Number(control.value)));
    else update(key as 'reducedMotion' | 'assistMode' | 'showFps', control.checked);
  });
  section.querySelector('[data-action="back"]')!.addEventListener('click', onBack);
  queueMicrotask(() => section.querySelector<HTMLElement>('[data-setting]')?.focus());
  return section;
}
